import { useState } from 'react';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IRapportLineControl } from './RapportMallTypes';
import { MM_TO_PIXEL_SCALE, SELECTED_SHADOW } from './RapportMallUtils';
import { RapportKontrollHeader } from './RapportKontrollHeader';
import { RapporteditorTextField } from '../Rapporteditor/RapporteditorTextField';
import EDTypography from '../Typography/EDTypography';

export interface IProps {
  ctrl: IRapportLineControl;
  sx?: any;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSelect?: (path: number[]) => void;
  onUpdate?: (updatedControl: IRapportLineControl) => void;
  controlPath: number[];
  selected?: boolean;
  maxWidth: number;
}

export const RapportLine: React.FC<IProps> = props => {
  const { ctrl, sx, onMouseEnter, onMouseLeave, selected, maxWidth } = props;
  const handleSelect = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    props.onSelect?.(props.controlPath);
  };

  const { x1, y1, x2, y2 } = ctrl;

  // Define line start and end points,
  // based on x1,y1 and x2,y2 with fallbacks
  let startX = typeof x1 === 'number' ? x1 : 0;
  let startY = typeof y1 === 'number' ? y1 : 0;
  let endX = typeof x2 === 'number' ? x2 : maxWidth; // full width if missing
  let endY = typeof y2 === 'number' ? y2 : startY; // horizontal if missing

  // Prevent negative or NaN widths
  if (!isFinite(startX)) startX = 0;
  if (!isFinite(endX)) endX = 0;
  if (endX < 0) endX = 0;

  // Compute bounding box (in mm) then transform to px
  const minX = Math.min(startX, endX);
  const minY = Math.min(startY, endY);
  const widthMM = Math.abs(endX - startX);
  const heightMM = Math.abs(endY - startY);

  const widthPx = widthMM * MM_TO_PIXEL_SCALE;
  const heightPx = heightMM * MM_TO_PIXEL_SCALE;

  // Line start relative to bounding box
  const relStartX = (startX - minX) * MM_TO_PIXEL_SCALE;
  const relStartY = (startY - minY) * MM_TO_PIXEL_SCALE;
  const relEndX = (endX - minX) * MM_TO_PIXEL_SCALE;
  const relEndY = (endY - minY) * MM_TO_PIXEL_SCALE;

  // padding to make selection box a bit larger than line itself
  const PAD = 3; // px

  return (
    <Box
      sx={{
        position: 'absolute',
        left: minX * MM_TO_PIXEL_SCALE - PAD,
        top: minY * MM_TO_PIXEL_SCALE - PAD,
        width: `${widthPx + 2 * PAD}px`,
        height: `${heightPx + 2 * PAD - 1}px`,
        background: 'transparent',
        boxSizing: 'border-box',
        boxShadow: selected ? SELECTED_SHADOW : 'none',
        cursor: 'pointer',
        ...sx,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleSelect}
      data-control-path={props.controlPath.join('-')}
    >
      <svg
        width={widthPx + PAD * 2}
        height={heightPx + PAD * 2}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <line
          x1={relStartX + PAD}
          y1={relStartY + PAD}
          x2={relEndX + PAD}
          y2={relEndY + PAD}
          stroke="#aaa"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
          shapeRendering="crispEdges"
        />
      </svg>
    </Box>
  );
};

export interface IRapportLineEditProps {
  value: IRapportLineControl;
  onChange: (ctrl: IRapportLineControl) => void;
}

export const RapportLineEdit: React.FC<IRapportLineEditProps> = props => {
  // Check if advanced mode should be enabled based on field values
  const hasAdvancedValues = Boolean(
    (props.value.x1 !== undefined && props.value.x1 !== null) ||
      (props.value.x2 !== undefined && props.value.x2 !== null) ||
      (props.value.y2 !== undefined && props.value.y2 !== null)
  );

  const [isAdvancedMode, setIsAdvancedMode] = useState(hasAdvancedValues);

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.checked;
    setIsAdvancedMode(newMode);

    // If switching to simple mode, clear advanced fields
    if (!newMode) {
      props.onChange({
        ...props.value,
        x1: undefined,
        x2: undefined,
        y2: undefined,
      });
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <RapportKontrollHeader value={props.value} />

      <FormControlLabel
        control={
          <Switch
            checked={isAdvancedMode || hasAdvancedValues}
            onChange={handleModeChange}
            disabled={hasAdvancedValues}
          />
        }
        label={
          isAdvancedMode || hasAdvancedValues
            ? 'Avancerad linje'
            : 'Enkel linje'
        }
        sx={{ mb: 2 }}
      />

      {isAdvancedMode || hasAdvancedValues ? (
        <Box>
          <EDTypography sx={{ mb: 1 }}>Från koordinat (mm):</EDTypography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <RapporteditorTextField
              label="x1"
              field="x1"
              value={props.value}
              onChange={props.onChange}
              numeric
            />
            <RapporteditorTextField
              label="y1"
              field="y1"
              value={props.value}
              onChange={props.onChange}
              numeric
            />
          </Box>
          <EDTypography sx={{ mb: 1 }}>Till koordinat (mm):</EDTypography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <RapporteditorTextField
              label="x2"
              field="x2"
              value={props.value}
              onChange={props.onChange}
              numeric
            />
            <RapporteditorTextField
              label="y2"
              field="y2"
              value={props.value}
              onChange={props.onChange}
              numeric
            />
          </Box>
        </Box>
      ) : (
        <Box>
          <RapporteditorTextField
            label="Topp"
            field="y1"
            value={props.value}
            onChange={props.onChange}
            numeric
          />
        </Box>
      )}
    </Box>
  );
};
